const baseUrl = process.env.VUE_APP_BASE_API || '/prod-api'

// 401/403 跳登录的统一处理(避免每个组件都重复写)
function redirectToLogin() {
  if (typeof window === 'undefined') return
  // 防止多次重定向叠加
  if (window.__ainovelAuthRedirecting) return
  window.__ainovelAuthRedirecting = true
  try { localStorage.removeItem('ainovel-token') } catch (_) { void _ }
  // 用 hash 路由跳转登录页(基于现有结构)
  const here = window.location.pathname + window.location.search
  if (!/\/login/.test(here)) {
    window.location.href = '/#/login?from=' + encodeURIComponent(here)
    // 如果不是 hash 模式(直接在根),也强制走 location.assign
    setTimeout(() => { window.__ainovelAuthRedirecting = false }, 1000)
  } else {
    window.__ainovelAuthRedirecting = false
  }
}

async function request(path, options = {}) {
  const token = localStorage.getItem('ainovel-token')
  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {})
    }
  })
  if (response.status === 401 || response.status === 403) {
    let data = {}
    try { data = await response.json() } catch (_) { void _ }
    if (path === '/login' || path === '/register' || path === '/register/email-code') {
      throw new Error(data.msg || '认证失败')
    }
    redirectToLogin()
    const err = new Error(data.msg || (response.status === 401 ? '登录已过期,请重新登录' : '没有访问权限'))
    err.code = response.status
    throw err
  }
  const data = await response.json()
  // 防御:即使后端没设 HTTP 401(老逻辑返回 200 + body.code=401/403),也要识别并跳登录
  if (data && (data.code === 401 || data.code === 403)) {
    if (path === '/login' || path === '/register' || path === '/register/email-code') {
      throw new Error(data.msg || '认证失败')
    }
    redirectToLogin()
    const err = new Error(data.msg || (data.code === 401 ? '登录已过期,请重新登录' : '没有访问权限'))
    err.code = data.code
    throw err
  }
  if (!response.ok || (data.code !== undefined && data.code !== 200)) {
    throw new Error(data.msg || '请求失败，请稍后重试')
  }
  return data
}

function parseSseBlock(block) {
  let event = 'message'
  const data = []
  block.split(/\r?\n/).forEach(line => {
    if (line.startsWith('event:')) event = line.slice(6).trim()
    if (line.startsWith('data:')) data.push(line.slice(5).trimStart())
  })
  if (!data.length) return null
  const raw = data.join('\n')
  let payload
  try { payload = JSON.parse(raw) } catch (_) { payload = { text: raw } }
  return { event, data: payload }
}

async function streamRequest(path, payload, onEvent) {
  const token = localStorage.getItem('ainovel-token')
  const response = await fetch(`${baseUrl}${path}`, {
    method: 'POST', body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  })
  if (!response.ok || !response.body) {
    let data = {}
    try { data = await response.json() } catch (_) { data = {} }
    throw new Error(data.msg || `AI 流式请求失败（${response.status}）`)
  }
  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''
  let finished = false
  while (!finished) {
    const { value, done } = await reader.read()
    finished = done
    buffer += decoder.decode(value || new Uint8Array(), { stream: !finished })
    const blocks = buffer.split(/\r?\n\r?\n/)
    buffer = blocks.pop() || ''
    for (const block of blocks) {
      const message = parseSseBlock(block)
      if (!message) continue
      if (message.event === 'error') throw new Error(message.data.message || 'AI 编辑失败')
      onEvent(message)
    }
  }
  if (buffer.trim()) {
    const message = parseSseBlock(buffer)
    if (message?.event === 'error') throw new Error(message.data.message || 'AI 编辑失败')
    if (message) onEvent(message)
  }
}

async function streamGetRequest(path, onEvent, options = {}) {
  const token = localStorage.getItem('ainovel-token')
  const response = await fetch(`${baseUrl}${path}`, {
    method: 'GET',
    headers: { Accept: 'text/event-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    signal: options.signal
  })
  if (!response.ok || !response.body) {
    let data = {}
    try { data = await response.json() } catch (_) { data = {} }
    throw new Error(data.msg || `AI 流式请求失败（${response.status}）`)
  }
  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''
  let finished = false
  while (!finished) {
    const { value, done } = await reader.read()
    finished = done
    buffer += decoder.decode(value || new Uint8Array(), { stream: !finished })
    const blocks = buffer.split(/\r?\n\r?\n/)
    buffer = blocks.pop() || ''
    for (const block of blocks) {
      const message = parseSseBlock(block)
      if (!message) continue
      if (message.event === 'error') throw new Error(message.data.message || 'AI 编辑失败')
      onEvent(message)
    }
  }
  if (buffer.trim()) {
    const message = parseSseBlock(buffer)
    if (message?.event === 'error') throw new Error(message.data.message || 'AI 编辑失败')
    if (message) onEvent(message)
  }
}

async function downloadRequest(path, fallbackName) {
  const token = localStorage.getItem('ainovel-token')
  const response = await fetch(`${baseUrl}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
  if (!response.ok) throw new Error('导出失败，请稍后重试')
  const disposition = response.headers.get('Content-Disposition') || ''
  const match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  const fileName = match ? decodeURIComponent(match[1]) : fallbackName
  const url = URL.createObjectURL(await response.blob())
  const link = document.createElement('a')
  link.href = url; link.download = fileName; document.body.appendChild(link); link.click(); link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
  return fileName
}

export const authApi = {
  captcha: () => request('/captchaImage'),
  login: payload => request('/login', { method: 'POST', body: JSON.stringify(payload) }),
  register: payload => request('/register', { method: 'POST', body: JSON.stringify(payload) }),
  sendEmailCode: payload => request('/register/email-code', { method: 'POST', body: JSON.stringify(payload) })
}

export const clientApi = {
  dashboard: () => request('/novel/client/dashboard'),
  works: (keyword = '') => request(`/novel/client/works${keyword ? `?keyword=${encodeURIComponent(keyword)}` : ''}`),
  work: id => request(`/novel/client/works/${id}`),
  createWork: payload => request('/novel/client/works', { method: 'POST', body: JSON.stringify(payload) }),
  updateWork: (id, payload) => request(`/novel/client/works/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  adjustChapterCount: (id, chapterCount) => request(`/novel/client/works/${id}/chapter-count`, { method: 'PUT', body: JSON.stringify({ chapterCount }) }),
  removeWork: id => request(`/novel/client/works/${id}`, { method: 'DELETE' }),
  workspace: id => request(`/novel/client/works/${id}/workspace`),
  generateSetup: (id, payload = {}) => request(`/novel/client/works/${id}/generate-setup`, { method: 'POST', body: JSON.stringify(payload) }),
  cancelSetup: (id) => request(`/novel/client/works/${id}/cancel-setup`, { method: 'POST' }),
  generateNextChapter: (id, payload = {}) => request(`/novel/client/works/${id}/generate-next-chapter`, { method: 'POST', body: JSON.stringify(payload) }),
  generateAllChapters: (id, payload = {}) => request(`/novel/client/works/${id}/generate-all-chapters`, { method: 'POST', body: JSON.stringify({...payload, async: true}) }),
  cancelBatchGeneration: (batchId) => request(`/novel/client/works/batch-task/${batchId}/cancel`, { method: 'POST' }),
  getBatchStatus: (batchId) => request(`/novel/client/works/batch-task/${batchId}`),
  batchTasks: (workId) => request(`/novel/client/works/${workId}/batch-tasks`),
  createSetting: (id, resource, payload) => request(`/novel/client/works/${id}/settings/${resource}`, { method: 'POST', body: JSON.stringify(payload) }),
  updateSetting: (id, resource, settingId, payload) => request(`/novel/client/works/${id}/settings/${resource}/${settingId}`, { method: 'PUT', body: JSON.stringify(payload) }),
  removeSetting: (id, resource, settingId) => request(`/novel/client/works/${id}/settings/${resource}/${settingId}`, { method: 'DELETE' }),
  createCharacterRelation: (id, payload) => request(`/novel/client/works/${id}/character-relations`, { method: 'POST', body: JSON.stringify(payload) }),
  updateCharacterRelation: (id, relationId, payload) => request(`/novel/client/works/${id}/character-relations/${relationId}`, { method: 'PUT', body: JSON.stringify(payload) }),
  removeCharacterRelation: (id, relationId) => request(`/novel/client/works/${id}/character-relations/${relationId}`, { method: 'DELETE' }),
  createCharacterForeshadow: (id, payload) => request(`/novel/client/works/${id}/character-foreshadows`, { method: 'POST', body: JSON.stringify(payload) }),
  updateCharacterForeshadow: (id, linkId, payload) => request(`/novel/client/works/${id}/character-foreshadows/${linkId}`, { method: 'PUT', body: JSON.stringify(payload) }),
  removeCharacterForeshadow: (id, linkId) => request(`/novel/client/works/${id}/character-foreshadows/${linkId}`, { method: 'DELETE' }),
  createChapter: (workId, payload) => request(`/novel/client/works/${workId}/chapters`, { method: 'POST', body: JSON.stringify(payload) }),
  chapter: id => request(`/novel/client/chapters/${id}`),
  saveDraft: (id, payload) => request(`/novel/client/chapters/${id}/draft`, { method: 'PUT', body: JSON.stringify(payload) }),
  confirmChapter: (id, payload) => request(`/novel/client/chapters/${id}/confirm`, { method: 'POST', body: JSON.stringify(payload) }),
  aiAction: (id, payload) => request(`/novel/client/chapters/${id}/ai-action`, { method: 'POST', body: JSON.stringify(payload) }),
  aiActionStream: (id, payload, onEvent) => streamRequest(`/novel/client/chapters/${id}/ai-action-stream`, payload, onEvent),
  aiChat: (id, payload) => request(`/novel/client/chapters/${id}/ai-chat`, { method: 'POST', body: JSON.stringify(payload) }),
  aiChatStream: (id, payload, onEvent) => streamRequest(`/novel/client/chapters/${id}/ai-chat-stream`, payload, onEvent),
  aiRewriteChapters: (id, payload) => request(`/novel/client/works/${id}/ai-rewrite-chapters`, { method: 'POST', body: JSON.stringify(payload) }),
  aiRewriteChaptersStream: (id, payload, onEvent) => streamRequest(`/novel/client/works/${id}/ai-rewrite-chapters-stream`, payload, onEvent),
  // Phase C 章节大纲编排(需求 6)
  updateChapterOutline: (workId, chapterId, payload) => request(`/novel/client/works/${workId}/chapters/${chapterId}/outline`, { method: 'PUT', body: JSON.stringify(payload) }),
  aiRewriteChapterOutline: (workId, chapterId, payload) => request(`/novel/client/works/${workId}/chapters/${chapterId}/outline/ai-rewrite`, { method: 'POST', body: JSON.stringify(payload) }),
  aiBatchRewriteOutlines: (workId, payload) => request(`/novel/client/works/${workId}/chapters/outlines/ai-batch-rewrite`, { method: 'POST', body: JSON.stringify(payload) }),
  aiChatRewriteOutlines: (workId, payload) => request(`/novel/client/works/${workId}/chapters/outlines/ai-chat-rewrite`, { method: 'POST', body: JSON.stringify(payload) }),
  aiRegenerateWholeOutline: (workId, payload) => request(`/novel/client/works/${workId}/chapters/outlines/ai-regenerate`, { method: 'POST', body: JSON.stringify(payload) }),
  applyOutlineVersion: (workId, chapterId, versionId) => request(`/novel/client/works/${workId}/chapters/${chapterId}/outline/versions/${versionId}/apply`, { method: 'POST' }),
  applyOutlineVersions: (workId, versionIds) => request(`/novel/client/works/${workId}/chapters/outlines/versions/apply`, { method: 'POST', body: JSON.stringify({ versionIds }) }),
  outlineQuality: workId => request(`/novel/client/works/${workId}/chapters/outlines/quality`),
  // Phase D-3 支线剧情管理(需求 3)
  listStoryTrees: workId => request(`/novel/client/works/${workId}/story-trees`),
  createStoryTree: (workId, payload) => request(`/novel/client/works/${workId}/story-trees`, { method: 'POST', body: JSON.stringify(payload) }),
  deleteStoryTree: (workId, treeId) => request(`/novel/client/works/${workId}/story-trees/${treeId}`, { method: 'DELETE' }),
  listStoryNodes: (workId, treeId) => request(`/novel/client/works/${workId}/story-trees/${treeId}/nodes`),
  listStoryNodeLinks: (workId, treeId) => request(`/novel/client/works/${workId}/story-trees/${treeId}/links`),
  createStoryNode: (workId, treeId, payload) => request(`/novel/client/works/${workId}/story-trees/${treeId}/nodes`, { method: 'POST', body: JSON.stringify(payload) }),
  updateStoryNode: (workId, nodeId, payload) => request(`/novel/client/works/${workId}/story-nodes/${nodeId}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteStoryNode: (workId, nodeId) => request(`/novel/client/works/${workId}/story-nodes/${nodeId}`, { method: 'DELETE' }),
  createStoryNodeLink: (workId, treeId, payload) => request(`/novel/client/works/${workId}/story-trees/${treeId}/links`, { method: 'POST', body: JSON.stringify(payload) }),
  deleteStoryNodeLink: (workId, linkId) => request(`/novel/client/works/${workId}/story-node-links/${linkId}`, { method: 'DELETE' }),
  startMultiAgent: (workId, chapterId) => request(`/novel/client/works/${workId}/chapters/${chapterId}/multi-agent-start`, { method: 'POST' }),
  startFinalAudit: workId => request(`/novel/client/works/${workId}/final-audit`, { method: 'POST' }),
  finalAudits: workId => request(`/novel/client/works/${workId}/final-audits`),
  coverStatus: workId => request(`/novel/client/works/${workId}/cover`),
  generateCover: workId => request(`/novel/client/works/${workId}/cover`, { method: 'POST' }),
  downloadCover: workId => downloadRequest(`/novel/client/works/${workId}/cover/download`, `novel-${workId}-600x800.png`),
  multiAgentStream: (workId, taskId, onEvent) => streamGetRequest(`/novel/client/works/${workId}/multi-agent-stream/${taskId}`, onEvent),
  multiAgentTask: taskId => request(`/novel/client/multi-agent-tasks/${taskId}`),
  multiAgentTasks: (workId, chapterId) => request(`/novel/client/works/${workId}/chapters/${chapterId}/multi-agent-tasks`),
  // SSE 实时进度:打开流后由回调收到 current/progress/complete/failed/ping 事件
  // 用 fetch + ReadableStream(不依赖 EventSource,因为 EventSource 不支持自定义 Authorization header)
  subscribeBatchProgress: (workId, onEvent, options = {}) => streamGetRequest(`/novel/client/works/${workId}/batch-progress/stream`, onEvent, options),
  // 轻量进度查询 (createWork 异步模式, 5s 轮询一次)
  outlineProgress: (workId) => request(`/novel/client/works/${workId}/outline-progress`),
  exportChapter: (id, format) => downloadRequest(`/novel/client/chapters/${id}/export?format=${format}`, `chapter.${format}`),
  exportNovel: (id, format) => downloadRequest(`/novel/client/works/${id}/export?format=${format}`, `novel.${format}`),
  materials: () => request('/novel/client/materials'),
  membership: () => request('/novel/client/membership'),
  profile: () => request('/novel/client/profile'),
  updateProfile: payload => request('/novel/client/profile', { method: 'PUT', body: JSON.stringify(payload) })
}

/**
 * 会员体系 v2 — 套餐/Token/支付 (后端 /novel/member + /novel/token + /novel/payment)
 * 浏览器从 AjaxResult 拿 data, 业务字段都在 data.* 下面
 */
export const memberApi = {
  // 套餐
  listPlans: () => request('/novel/member/plans'),
  getPlan: id => request(`/novel/member/plan/${id}`),
  getCurrentSubscription: () => request('/novel/member/current'),
  getHistory: (limit = 20) => request(`/novel/member/history?limit=${limit}`),
  // Token 包 + 余额 + 流水
  listPacks: () => request('/novel/token/packs'),
  getBalance: () => request('/novel/token/balance'),
  getLedger: (offset = 0, limit = 20) => request(`/novel/token/ledger?offset=${offset}&limit=${limit}`),
  // 支付
  getPaymentChannels: () => request('/novel/payment/channels'),
  createMemberOrder: (planId, payMethod, pointsToUse = 0) => request('/novel/payment/member/order', { method: 'POST', body: JSON.stringify({ planId, payMethod, pointsToUse, platform: 'PC' }) }),
  createPackOrder: (packId, payMethod, pointsToUse = 0) => request('/novel/payment/pack/order', { method: 'POST', body: JSON.stringify({ packId, payMethod, pointsToUse, platform: 'PC' }) }),
  mockPaid: orderNo => request(`/novel/payment/mock-paid/${orderNo}`, { method: 'POST' }),
  queryOrder: orderNo => request(`/novel/payment/order/${orderNo}`)
}

export const referralApi = {
  overview: () => request('/novel/referral/me'),
  ledger: (offset = 0, limit = 20) => request(`/novel/referral/ledger?offset=${offset}&limit=${limit}`)
}
